import { UUID } from "crypto";

import { uploadFileToS3 } from "@perceptron/api/externalApiClient";
import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { downscaleAndConvertImage } from "@perceptron/core";
import { generateUUID } from "@perceptron/utils/crypto";
import { isSupportedMediaType, MediaUploadItemResponse } from "@perceptron/utils/mediaUpload";
import { getFileExtension } from "@perceptron/utils/mime";
import { Editor } from "@tiptap/core";
import { toast } from "sonner";

import { env } from "@demo/env";
import { trackMedia } from "@demo/libs/analytics/utils";
import { demoS3Api } from "@demo/libs/api/demoApiClient";
import { MediaFactory } from "@demo/libs/media-factory";
import { S3BatchDownloadResponse, S3BatchUploadResponse } from "@demo/server/api/contracts/s3Presigned";
import { useMediaStore } from "@demo/store/media";
import { useUploadProgressStore } from "@demo/store/upload-progress";

interface MediaItem {
  key: UUID;
  file: File;
}

export class MediaUploadService {
  private static readonly BATCH_SIZE = 10; // Configure batch size for parallel uploads

  /**
   * Process media files and add them to a media set
   */
  static async processMediaFiles(files: File[], mediaSetId: string, editor: Editor): Promise<void> {
    if (files.length === 0) return;

    const uploadProgress = useUploadProgressStore.getState();

    // Check for existing upload
    if (uploadProgress.getUploadProgress(mediaSetId)?.isUploading) {
      console.warn(`Upload already in progress for mediaSetId: ${mediaSetId}`);
      return;
    }

    // Filter first so pendingPreviews indices line up 1:1 with the files we
    // actually upload — uploadFilesToS3 reports byte progress by absolute file
    // index, and that index is used to update pendingPreviews[i].
    const validFiles = this.filterValidFiles(files);
    if (validFiles.length === 0) return;

    uploadProgress.startUpload(mediaSetId, validFiles);

    try {
      await this.processWithS3Upload(validFiles, mediaSetId, editor, uploadProgress);

      // Track uploads with per-file media kind detection
      validFiles.forEach((file) => {
        const mediaKind = MediaFactory.detectMediaKindFromFile(file);
        if (mediaKind) {
          trackMedia.upload(mediaKind, file.size, file.name);
        }
      });
    } catch (error) {
      console.error(`Error processing files:`, error);
      throw error;
    } finally {
      uploadProgress.completeUpload(mediaSetId);
    }
  }

  /**
   * Upload a single file to S3 and return its download URL. Mode-agnostic — no
   * editor / media-set / progress coupling — for callers that manage their own
   * media state (e.g. Detect). The caller is responsible for any downscaling.
   */
  static async uploadFile(file: File): Promise<{ url: string; key: UUID; extension: string }> {
    const mediaItems: MediaItem[] = [{ key: generateUUID(), file }];
    const uploadUrls = await this.getUploadUrls(mediaItems);
    const [uploadMeta] = this.createUploadsMetadata(mediaItems, uploadUrls);
    if (!uploadMeta) throw new Error("Failed to create upload metadata");

    await uploadFileToS3(uploadMeta.uploadUrl, uploadMeta.file);

    const downloadUrls = await this.getDownloadUrls([uploadMeta]);
    const url = downloadUrls.get(uploadMeta.mediaUuid);
    if (!url) throw new Error("Failed to resolve download URL");

    return { url, key: uploadMeta.mediaUuid, extension: uploadMeta.extension };
  }

  /**
   * Filter out invalid files and return only supported ones
   */
  private static filterValidFiles(files: File[]): File[] {
    const supportedPrefixes = MediaFactory.getAllSupportedPrefixes();
    return files.filter((file) => file && isSupportedMediaType(file, supportedPrefixes));
  }

  /**
   * Process files using S3 upload
   */
  private static async processWithS3Upload(
    files: File[],
    mediaSetId: string,
    editor: Editor,
    uploadProgress: ReturnType<typeof useUploadProgressStore.getState>,
  ): Promise<void> {
    // Process files - downscale images but upload videos directly
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        // Only downscale images, videos are uploaded as-is
        if (file.type.startsWith("image/")) {
          return downscaleAndConvertImage(file, {
            patchSize: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_PATCH_SIZE,
            maxNumPatches: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_MAX_NUM_PATCHES,
            epsilon: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_EPSILON,
            pixelShuffleScale: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_PIXEL_SHUFFLE_SCALE,
            resultQuality: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_RESULT_QUALITY,
          });
        }
        // Videos are uploaded directly without processing
        return file;
      }),
    );

    const mediaItems: MediaItem[] = processedFiles.map((file) => ({
      key: generateUUID(),
      file,
    }));

    try {
      // Get upload URLs
      const uploadUrls = await this.getUploadUrls(mediaItems);
      const uploadsMetadata = this.createUploadsMetadata(mediaItems, uploadUrls);

      // Upload files to S3
      const successfulUploads = await this.uploadFilesToS3(uploadsMetadata, mediaSetId, uploadProgress, files.length);

      if (successfulUploads.length === 0) {
        console.warn("No files were successfully uploaded to S3");
        return;
      }

      // Get download URLs
      const downloadUrls = await this.getDownloadUrls(successfulUploads);

      // Add to media store and editor
      await this.addUploadedMedia(successfulUploads, downloadUrls, mediaSetId, editor);
    } catch (error) {
      console.error(`Error processing media:`, error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An unexpected error occurred");
      }
    }
  }

  /**
   * Get batch upload URLs from the server
   */
  private static async getUploadUrls(mediaItems: MediaItem[]): Promise<string[]> {
    const response: S3BatchUploadResponse = await demoS3Api.batchGenerateUploadUrls({
      files: mediaItems.map((item) => ({
        key: item.key,
        contentType: item.file.type,
        contentLength: item.file.size,
      })),
    });

    return response.urls;
  }

  /**
   * Create upload metadata for each file
   */
  private static createUploadsMetadata(mediaItems: MediaItem[], uploadUrls: string[]): MediaUploadItemResponse[] {
    return uploadUrls.map((url, index) => {
      const mediaItem = mediaItems[index];
      if (!mediaItem) {
        throw new Error(`Media item at index ${index} is not defined`);
      }

      return {
        mediaUuid: mediaItem.key,
        uploadUrl: url,
        contentType: mediaItem.file.type,
        file: mediaItem.file,
        extension: getFileExtension(mediaItem.file),
      };
    });
  }

  /**
   * Upload files to S3 in batches
   */
  private static async uploadFilesToS3(
    uploadsMetadata: MediaUploadItemResponse[],
    mediaSetId: string,
    uploadProgress: ReturnType<typeof useUploadProgressStore.getState>,
    totalFiles: number,
  ): Promise<MediaUploadItemResponse[]> {
    const successfulUploads: MediaUploadItemResponse[] = [];
    let uploadedCount = 0;

    // Process uploads in batches to avoid overwhelming the system
    for (let batchStart = 0; batchStart < uploadsMetadata.length; batchStart += this.BATCH_SIZE) {
      const batch = uploadsMetadata.slice(batchStart, batchStart + this.BATCH_SIZE);

      const batchPromises = batch.map(async (metadata, batchIdx) => {
        // Absolute file slot — lines up with pendingPreviews[fileIndex] in the store.
        const fileIndex = batchStart + batchIdx;
        try {
          await uploadFileToS3(metadata.uploadUrl, metadata.file, {
            onProgress: (loaded, total) => {
              uploadProgress.updatePreviewProgress(mediaSetId, fileIndex, loaded, total);
            },
          });
          uploadedCount++;
          uploadProgress.updateProgress(mediaSetId, uploadedCount, totalFiles);
          uploadProgress.markPreviewDone(mediaSetId, fileIndex);
          return metadata;
        } catch (error) {
          console.error(`Failed to upload file ${metadata.file.name}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      successfulUploads.push(...(batchResults.filter(Boolean) as MediaUploadItemResponse[]));
    }

    return successfulUploads;
  }

  /**
   * Get download URLs for successfully uploaded files
   */
  private static async getDownloadUrls(uploads: MediaUploadItemResponse[]): Promise<Map<UUID, string>> {
    const response: S3BatchDownloadResponse = await demoS3Api.batchGenerateDownloadUrls({
      keys: uploads.map((upload) => upload.mediaUuid),
    });

    return new Map(response.urls.map((url, index) => [uploads[index]!.mediaUuid, url]));
  }

  /**
   * Add successfully uploaded media to the store and editor
   */
  private static async addUploadedMedia(
    uploads: MediaUploadItemResponse[],
    downloadUrls: Map<UUID, string>,
    mediaSetId: string,
    editor: Editor,
  ): Promise<void> {
    const { addMedia } = useMediaStore.getState();

    for (const upload of uploads) {
      const downloadUrl = downloadUrls.get(upload.mediaUuid);

      addMedia(mediaSetId, {
        data: "",
        extension: upload.extension,
        key: upload.mediaUuid,
        url: downloadUrl,
      });

      editor.commands.addMediaToSet({
        containerId: mediaSetId,
        mediaId: upload.mediaUuid,
      });
    }

    // In the same synchronous block as the adds above, so React commits one
    // render for both. Clearing later — from the caller's `finally`, past an
    // await — lets a render land in between, where every uploaded file counts
    // twice: once as a real tile and once as its own pending preview. At six
    // files into a two-row set that shows a collapsed grid and a wrong "+N"
    // that then snaps back. The `finally` still runs, and is a no-op by then.
    useUploadProgressStore.getState().completeUpload(mediaSetId);
  }

  /**
   * Check if a position is inside a MediaSetContainer
   */
  static findMediaSetContainerAtPosition(
    editor: Editor,
    pos: number,
  ): { node: import("@tiptap/pm/model").Node; pos: number } | null {
    const { state } = editor;
    const $pos = state.doc.resolve(pos);

    // Check if we're directly on a MediaSetContainer
    const nodeAt = state.doc.nodeAt(pos);
    if (nodeAt?.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER) {
      return { node: nodeAt, pos };
    }

    // Check parent nodes up to the root
    for (let depth = $pos.depth; depth >= 0; depth--) {
      const node = $pos.node(depth);
      if (node.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER) {
        return { node, pos: $pos.before(depth) };
      }
    }

    return null;
  }
}
